import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGeocodeAddress } from "@/features/maps/hooks/use-maps";
import type { GeocodingResult } from "@/features/maps/types";
import { cn } from "@/lib/utils";
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  PencilSimple,
  XCircleIcon,
} from "@phosphor-icons/react";
import * as React from "react";
import { useTranslation } from "@/features/utils/i18n";

interface AddressInputProps extends Omit<
  React.ComponentProps<"input">,
  "onChange" | "value" | "onSelect"
> {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (result: GeocodingResult) => void;
  autoVerify?: boolean;
  name?: string;
  required?: boolean;
}

export const AddressInput = React.forwardRef<
  HTMLInputElement,
  AddressInputProps
>(
  (
    {
      className,
      value,
      onChange,
      onSelect,
      autoVerify = true,
      name,
      required,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = React.useState(value || "");
    const [showResults, setShowResults] = React.useState(false);
    const [hasVerified, setHasVerified] = React.useState(false);
    const [validatedAddress, setValidatedAddress] = React.useState(value || "");
    const [isEditMode, setIsEditMode] = React.useState(!value);
    const [initialValue] = React.useState(value || "");
    const containerRef = React.useRef<HTMLDivElement>(null);

    const geocodeMutation = useGeocodeAddress();

    // Sync external value changes
    React.useEffect(() => {
      if (value !== undefined && value !== inputValue) {
        setInputValue(value);
        // Don't clear validation if value is being set from outside (e.g., reset)
        if (!value) {
          setHasVerified(false);
          setValidatedAddress("");
          setIsEditMode(true);
        }
      }
    }, [value, inputValue]);

    const handleVerify = React.useCallback(
      async (autoSelect = false) => {
        if (!inputValue.trim()) return;

        setHasVerified(false);
        setShowResults(false);

        try {
          const response = await geocodeMutation.mutateAsync(inputValue);
          if (response.results && response.results.length > 0) {
            setHasVerified(true);

            // Auto-select best match if requested (e.g., on blur)
            if (autoSelect) {
              const bestMatch = response.results[0];
              setInputValue(bestMatch.formatted);
              setValidatedAddress(bestMatch.formatted);
              onChange?.(bestMatch.formatted);
              onSelect?.(bestMatch);
              setShowResults(false);
              setIsEditMode(false);
            } else {
              setShowResults(true);
            }
          }
        } catch (error) {
          console.error("Geocoding error:", error);
        }
      },
      [inputValue, geocodeMutation, onChange, onSelect],
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange?.(newValue);
      setHasVerified(false);
      setShowResults(false);
      setValidatedAddress(""); // Clear validation when user types
    };

    const handleBlur = () => {
      // Don't auto-verify if value hasn't changed from initial
      const hasChanged = inputValue.trim() !== initialValue.trim();

      // Delay to allow click on results
      setTimeout(() => {
        if (autoVerify && inputValue.trim() && !hasVerified && hasChanged) {
          handleVerify(true); // Auto-select best match on blur
        }
      }, 200);
    };

    const handleSelectResult = (result: GeocodingResult) => {
      setInputValue(result.formatted);
      setValidatedAddress(result.formatted);
      onChange?.(result.formatted);
      onSelect?.(result);
      setShowResults(false);
      setHasVerified(true);
      setIsEditMode(false);
    };

    const handleSelectChange = (value: string) => {
      if (value === "__change__") {
        // Go back to edit mode
        setIsEditMode(true);
        setValidatedAddress("");
        setHasVerified(false);
        return;
      }

      // Find the result that matches this value
      const result = results.find((r) => r.formatted === value);
      if (result) {
        handleSelectResult(result);
      }
    };

    // Close results when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setShowResults(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const results = geocodeMutation.data?.results || [];
    const isLoading = geocodeMutation.isPending;
    const hasError = geocodeMutation.isError;
    const hasResults = results.length > 0;

    return (
      <div ref={containerRef} className="relative space-y-2">
        {/* Hidden input for form validation */}
        {required && (
          <input
            type="text"
            name={name ? `${name}_validated` : "address_validated"}
            value={validatedAddress}
            required
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            readOnly
          />
        )}

        {!isEditMode && validatedAddress ? (
          // Select mode - show dropdown with current address and alternatives
          <div className="flex gap-2">
            <Select
              value={validatedAddress}
              onValueChange={handleSelectChange}
              disabled={props.disabled}
            >
              <SelectTrigger className={cn("flex-1", className)}>
                <div className="flex items-center gap-2 flex-1">
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                {/* Current address */}
                <SelectItem value={validatedAddress}>
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="size-4" weight="fill" />
                    <span>{validatedAddress}</span>
                  </div>
                </SelectItem>

                {/* Other alternatives if available */}
                {results.length > 1 && (
                  <>
                    <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium">
                      {t("common.address.otherSuggestions")}
                    </div>
                    {results
                      .filter((r) => r.formatted !== validatedAddress)
                      .map((result, index) => (
                        <SelectItem key={index} value={result.formatted}>
                          <div className="flex items-center gap-2">
                            <MapPinIcon className="size-4" weight="duotone" />
                            <span>{result.formatted}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </>
                )}

                {/* Change address option */}
                <div className="border-t mt-1 pt-1">
                  <SelectItem value="__change__">
                    <div className="flex items-center gap-2 text-primary">
                      <PencilSimple className="size-4" weight="bold" />
                      <span className="font-medium">
                        {t("common.address.change")}
                      </span>
                    </div>
                  </SelectItem>
                </div>
              </SelectContent>
            </Select>
          </div>
        ) : (
          // Edit mode - show input and verify button
          <>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  ref={ref}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={cn(
                    "pr-10",
                    hasVerified && !hasResults && "border-yellow-500",
                    hasError && "border-red-500",
                    className,
                  )}
                  {...props}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isLoading && (
                    <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  )}
                  {!isLoading && hasVerified && !hasResults && (
                    <XCircleIcon
                      className="size-5 text-yellow-500"
                      weight="fill"
                    />
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="default"
                onClick={() => handleVerify(false)} // Show dropdown on manual verify
                disabled={!inputValue.trim() || isLoading}
              >
                <MagnifyingGlassIcon className="size-4 mr-2" />
                {t("common.address.verify")}
              </Button>
            </div>

            {/* Results dropdown */}
            {showResults && hasResults && (
              <Card className="absolute z-50 w-full mt-1 p-2 shadow-lg max-h-[400px] overflow-y-auto">
                <div className="space-y-1">
                  {results.map((result, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectResult(result)}
                      className={cn(
                        "w-full text-left p-3 rounded-md transition-colors",
                        "hover:bg-accent focus:bg-accent focus:outline-none",
                        "group",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex-shrink-0">
                          <MapPinIcon
                            className="size-5 text-muted-foreground group-hover:text-primary transition-colors"
                            weight="duotone"
                          />
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="font-medium text-sm">
                            {result.formatted}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {result.city && (
                              <Badge variant="secondary" className="text-xs">
                                {result.city}
                              </Badge>
                            )}
                            {result.postcode && (
                              <Badge variant="secondary" className="text-xs">
                                {result.postcode}
                              </Badge>
                            )}
                            {result.country && (
                              <Badge variant="secondary" className="text-xs">
                                {result.country}
                              </Badge>
                            )}
                          </div>
                          {(result.address_line1 || result.address_line2) && (
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              {result.address_line1 && (
                                <div>{result.address_line1}</div>
                              )}
                              {result.address_line2 && (
                                <div>{result.address_line2}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* No results message */}
            {showResults && !hasResults && hasVerified && (
              <Card className="absolute z-50 w-full mt-1 p-4 shadow-lg">
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <XCircleIcon
                    className="size-5 text-yellow-500"
                    weight="fill"
                  />
                  <div>
                    <div className="font-medium text-foreground">
                      {t("common.address.noMatch")}
                    </div>
                    <div className="text-xs">
                      {t("common.address.noMatchDescription")}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    );
  },
);

AddressInput.displayName = "AddressInput";
